import {
  Asset,
  Button,
  Checkbox,
  InfoIcon,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@clearcover/atlas';
import {
  CoveredPartyPayloadType,
  CreateOrUpdateDriverResponseType,
  DriverType,
  GetPolicyDraftResponseType,
  GetPolicyResponseTypeV2,
} from '@clearcover/core-app-types';
import { DriverAccidentType } from '@clearcover/core-app-types/dist/endpoints/getPolicy/driver';
import { useAuth0 } from 'Auth';
import { draftEndpointModifier, PolicyContext } from 'Policies/utils';
import React, { FC, useContext, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Card, Column, FormInput, Row } from 'Shared/components';
import { DatePickerModal } from 'Shared/components/DatePickerModal';
import { useUnderwritingErrors } from 'Shared/hooks/useUnderwritingErrors';
import { useLazyApi } from 'Shared/services/Api';
import { useNewApiV2 } from 'Shared/services/Api/makeApiHttp';
import { formatDateShort, formatMoney } from 'Shared/utils';
import {
  DriverOptionsResponse,
  GET_DRIVER_VEHICLES_TO_PAYLOAD,
} from '../PolicyDriversSection/DriverForm';
import { makeCreateUpdateDriverPayload } from '../PolicyDriversSection/DriverForm/utils';
import styles from './style.module.css';
import { ConflictingDraftModal } from '../ConflictingDraftModal';

export type PolicyClaimsType = {
  policy: GetPolicyResponseTypeV2 | GetPolicyDraftResponseType['policyDraft'];
};

type DriverAccident = {
  id: string;
  challenge?: 'edited' | 'removed';
  source: string;
  accidentDate: string;
  rawAccidentDate: string;
  claimType: string | undefined;
  totalLoss: string;
  fault: string | undefined;
  isAtFault: boolean;
  driver: string;
  driverStatus: string;
  claimStatus?: string;
  driverId: string;
  classifications?: DriverAccidentType['classifications'];
};

type ClaimTypeEnum =
  | 'At Fault'
  | 'Not At Fault'
  | 'RA Only'
  | 'GLS Only'
  | 'COMP Only';

const lendingLossClaimClassificationType = (accident: DriverAccidentType) => {
  const classifications = accident.classifications;
  const claimClassificationType = classifications?.claimClassificationTypeV2;

  return classifications?.lendingLoss
    ? 'Lending Loss - ' + claimClassificationType
    : claimClassificationType;
};

const isAtFault = (isFault: boolean) => {
  return isFault ? 'At Fault' : 'Not At Fault';
};

const getAccidentDriver = (
  accident: DriverAccidentType,
  driverName: string,
  driverId: string
) => {
  const driverAccident: DriverAccident = {
    id: accident.id,
    challenge: accident.challenge,
    source: accident.source,
    accidentDate: formatDateShort(accident.accidentDate),
    rawAccidentDate: accident.accidentDate,
    claimType: lendingLossClaimClassificationType(accident),
    totalLoss: accident.damageAmount,
    fault: isAtFault(accident.isAtFault),
    isAtFault: accident.isAtFault,
    driver: !accident.classifications?.lendingLoss ? driverName : '',
    driverStatus: !accident.classifications?.lendingLoss
      ? 'Active'
      : 'non-listed driver',
    claimStatus: accident.claimStatus,
    driverId: driverId,
    classifications: accident.classifications,
  };
  return driverAccident;
};

export const PolicyClaimsSection: FC<PolicyClaimsType> = ({ policy }) => {
  const { drivers } = policy;
  const [editingClaim, setEditingClaim] = useState<DriverAccident | null>(null);
  const { systemOfRecord } = useContext(PolicyContext);
  const { user } = useAuth0();

  const accidentsDriver = drivers.flatMap(driver =>
    driver.accidents
      .filter(accident => accident.claimStatus !== 'Collapsed')
      .sort(
        (a, b) =>
          new Date(b.accidentDate).getTime() -
          new Date(a.accidentDate).getTime()
      )
      .map(accident => {
        const driverPerson = driver.person.name;
        const driverName = driverPerson.firstName + ' ' + driverPerson.lastName;
        return getAccidentDriver(accident, driverName, driver.id);
      })
  );

  const showEditDriverButtons =
    systemOfRecord === 'clearcover' &&
    (user?.isCarrierUser || user?.isSystemUser);

  return (
    <div>
      {
        <Card id={'claims-card'}>
          <h2>Claims</h2>
          <ul className={styles.list}>
            <li key="title" className={styles.headers}>
              <span className={styles.header}>CLAIM STATUS</span>
              <span className={styles.header}>SOURCE</span>
              <span className={styles.header}>ACCIDENT DATE</span>
              <span className={styles.header}>CLAIM TYPE</span>
              <span className={styles.header}>TOTAL LOSS</span>
              <span className={styles.header}>FAULT</span>
              <span className={styles.header}>DRIVER</span>
              <span className={styles.header}>DRIVER STATUS</span>
              <span className={styles.header}></span>
            </li>
            {accidentsDriver.length !== 0 ? (
              accidentsDriver.map(claim => {
                return (
                  <li
                    data-testid={`claim-${claim.id}`}
                    key={`${claim.id}`}
                    className={styles.claim}
                  >
                    <div className={styles.data}>{claim.claimStatus}</div>
                    <div className={styles.data}>
                      {claim.source.toUpperCase()}
                    </div>
                    <div className={styles.data}>{claim.accidentDate}</div>
                    <div className={styles.data}>{claim.claimType}</div>
                    <div className={styles.data}>
                      {formatMoney(claim.totalLoss)}
                    </div>
                    <div className={styles.data}>{claim.fault}</div>
                    <div className={styles.data} title={claim.driver}>
                      {claim.driver}
                    </div>
                    <div className={styles.data}>{claim.driverStatus}</div>
                    <div className={styles.data} style={{ textAlign: 'right' }}>
                      {showEditDriverButtons && (
                        <ConflictingDraftModal
                          onApprove={() => {
                            if (editingClaim) {
                              setEditingClaim(null);
                            } else {
                              setEditingClaim(claim);
                            }
                          }}
                          renderToggle={openModal => (
                            <Button
                              data-testid={'edit-claim-button'}
                              variant="secondary"
                              size="sm"
                              onClick={openModal}
                              disabled={!!editingClaim || !!claim.challenge}
                            >
                              Edit
                            </Button>
                          )}
                        />
                      )}
                    </div>
                  </li>
                );
              })
            ) : (
              <li data-testid="claim-dash" className={styles.claim}>
                <div className={styles.data}>-</div>
                <div className={styles.data}>-</div>
                <div className={styles.data}>-</div>
                <div className={styles.data}>-</div>
                <div className={styles.data}>-</div>
                <div className={styles.data}>-</div>
                <div className={styles.data}>-</div>
                <div className={styles.data}>-</div>
              </li>
            )}
          </ul>
        </Card>
      }
      {editingClaim && (
        <ChallengeClaimModal
          editingClaim={editingClaim}
          setEditingClaim={setEditingClaim}
          policy={policy}
        />
      )}
    </div>
  );
};

const ChallengeClaimModal: FC<{
  editingClaim: DriverAccident;
  setEditingClaim: React.Dispatch<React.SetStateAction<DriverAccident | null>>;
  policy: GetPolicyResponseTypeV2 | GetPolicyDraftResponseType['policyDraft'];
}> = ({ editingClaim, setEditingClaim, policy }) => {
  const isAddDriver = !(
    policy.drivers.find(({ id }) => id === editingClaim.driverId)
      ?.driversLicense.status === 'Learner'
  );

  const { details, flags, refetchHandler } = useContext(PolicyContext);
  const { id: policyId, draftId } = useParams<{
    id: string;
    draftId: string;
  }>();

  const [{ loading: submiting }, createOrUpdateDriver] = useLazyApi<
    CreateOrUpdateDriverResponseType
  >({
    method: 'POST',
    url: `/policies/${
      policy.policyDetails.policyId
    }/createOrUpdateDriver${draftEndpointModifier(
      details.draftType,
      flags.renewalQuotesEnabled
    )}`,
  });

  const [{ data: configDiscoveryParams }] = useNewApiV2<DriverOptionsResponse>({
    url: `/options/driver?configDiscoveryParams=${details.configDiscoveryParams}&productFilingIdentifier=${details.filingIdentifier}&isAddDriver=${isAddDriver}`,
  });

  const { setUnderwritingErrors, underwritingErrors } = useUnderwritingErrors();
  const history = useHistory();

  const [claimData, setClaimData] = useState<{
    accidentDate: string;
    claimClassificationType?: ClaimTypeEnum;
    damageAmount: string;
    isAtFault: boolean;
  }>({
    accidentDate: editingClaim.rawAccidentDate,
    claimClassificationType:
      editingClaim.classifications?.claimClassificationTypeV2,
    isAtFault: editingClaim.isAtFault,
    damageAmount: editingClaim.totalLoss,
  });

  const claimTypes: any = editingClaim.classifications?.lendingLoss
    ? {
        'At Fault': 'Lending Loss - At Fault',
        'Not At Fault': 'Lending Loss - Not At-Fault',
        'COMP Only': 'Lending Loss - COMP Only',
        'GLS Only': 'Lending Loss - Glass Only',
      }
    : {
        'RA Only': 'RA Only',
        'COMP Only': 'COMP Only',
        'GLS Only': 'Glass Only',
        'At Fault': 'At-Fault Accident',
        'Not At Fault': 'Not At-Fault Accident',
      };

  const handleChallenge = (action: 'edit' | 'remove') => {
    let driverToUpdate = {
      ...policy.drivers.find(({ id }) => id === editingClaim.driverId),
    };

    const rawRequestData: CoveredPartyPayloadType = GET_DRIVER_VEHICLES_TO_PAYLOAD(
      policy.vehicles,
      configDiscoveryParams,
      driverToUpdate as DriverType,
      details?.region
    );

    if (action === 'edit') {
      rawRequestData.reports.accidents[
        editingClaim.id as keyof typeof rawRequestData.reports.accidents
      ] = {
        challenge: 'edited',
        accidentDate: claimData.accidentDate,
        isAtFault: claimData.isAtFault ? 'yes' : 'no',
        damageAmount: claimData.damageAmount.length
          ? formatMoney(claimData.damageAmount, {
              includeCents: true,
              formatWithSymbol: false,
            })
          : '',
        classifications: editingClaim.classifications
          ? { claimClassificationTypeV2: claimData.claimClassificationType }
          : null,
      };
    } else if (action === 'remove') {
      rawRequestData.reports.accidents[
        editingClaim.id as keyof typeof rawRequestData.reports.accidents
      ].challenge = 'removed';
    }

    const payloadData = makeCreateUpdateDriverPayload({
      values: rawRequestData,
      type: 'update',
      requiredAccessStatements: details.requiredAccessStatements ?? false,
      excludedDriverOptionsQuestionsConfig:
        configDiscoveryParams.excludedDriverOptionsQuestionsConfig,
    });

    createOrUpdateDriver({ data: payloadData })
      .then(draft => {
        draftId === draft.data.draftId
          ? refetchHandler()
          : history.push(`/policies/${policyId}/draft/${draft.data.draftId}`);
      })
      .catch(status => {
        setUnderwritingErrors(status);
      });
  };

  const edited =
    claimData.accidentDate !== editingClaim.rawAccidentDate ||
    claimData.damageAmount !== editingClaim.totalLoss ||
    claimData.isAtFault !== editingClaim.isAtFault ||
    (editingClaim.classifications
      ? claimData.claimClassificationType !==
        editingClaim.classifications.claimClassificationTypeV2
      : false);

  return (
    <Modal
      controlled={true}
      open={!!editingClaim}
      handleClose={() => setEditingClaim(null)}
      variant={'xl'}
      data-testid="chalenge-claim-modal"
    >
      {closeModal => (
        <div
          data-testid={'chalenge-claim-form'}
          className={styles.modalContainer}
        >
          <ModalHeader className={styles.modalHeader}>
            <h5>Edit Claim</h5>
          </ModalHeader>
          <ModalContent
            style={{ padding: '16px' }}
            className={styles.modalContent}
          >
            <Row>
              <Column variant="quarter" style={{ marginBottom: '10px' }}>
                Source: <b>{editingClaim.source.toUpperCase()}</b>
              </Column>
              <Column variant="full" style={{ marginBottom: '10px' }}>
                Driver Name:{' '}
                <b className={styles.data}>{editingClaim.driver}</b>
              </Column>
            </Row>
            <Row>
              <Column variant="quarter">
                <DatePickerModal
                  showEffectiveTime={false}
                  label="Accident Date"
                  startDate={new Date(formatDateShort(claimData.accidentDate))}
                  onDateChange={date => {
                    setClaimData({
                      ...claimData,
                      accidentDate: date,
                    });
                  }}
                />
              </Column>
              {editingClaim.classifications && (
                <Column variant="quarter">
                  <FormInput
                    name="claim-type"
                    label="Select Claim Type"
                    data-testid="claim-type-field"
                    variant={'select'}
                    options={claimTypes}
                    value={claimData.claimClassificationType}
                    onChange={(value: string) => {
                      setClaimData({
                        ...claimData,
                        claimClassificationType: value as ClaimTypeEnum,
                      });
                    }}
                  />
                </Column>
              )}
              <Column variant="sixth">
                <FormInput
                  label="Loss Amount"
                  name="loss-amount"
                  data-testid="loss-amount-field"
                  variant="text"
                  type="number"
                  value={claimData.damageAmount}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setClaimData({
                      ...claimData,
                      damageAmount: event.currentTarget.value,
                    });
                  }}
                />
              </Column>
              <Column variant="third">
                <Checkbox
                  name="at-fault"
                  value={claimData.isAtFault}
                  label="This was an at-fault accident"
                  data-testid="accident-at-fault-field"
                  onChange={(value: boolean) =>
                    setClaimData({ ...claimData, isAtFault: value })
                  }
                  className={styles.atFaultCheckbox}
                />
              </Column>
            </Row>
            <div className={styles.info}>
              <Asset
                className={styles.infoIcon}
                icon={InfoIcon}
                height={16}
                width={16}
                description="info"
              />
              Please remember to submit proof in Zendesk when modifying claim
              data.
            </div>
            <p className={styles.warning}>Modifying data cannot be undone.</p>
            {underwritingErrors && (
              <p className={styles.warning}>{underwritingErrors}</p>
            )}
          </ModalContent>
          <ModalFooter>
            <div className={styles.modalButtonGroup}>
              <div>
                <Button
                  variant="primary"
                  style={{ marginRight: '16px' }}
                  onClick={() => handleChallenge('edit')}
                  data-testid="edit-claim-save-button"
                  disabled={!edited || submiting}
                >
                  Save
                </Button>
                <Button
                  variant="danger"
                  onClick={() => handleChallenge('remove')}
                  data-testid="edit-claim-remove-button"
                  disabled={submiting}
                >
                  Remove
                </Button>
              </div>
              <div>
                <Button variant="secondary" onClick={closeModal}>
                  Cancel
                </Button>
              </div>
            </div>
          </ModalFooter>
        </div>
      )}
    </Modal>
  );
};

export default PolicyClaimsSection;
